import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {
  DsAggProduct,
  DsKeyword,
  DsProduct,
  DsResearch,
  DsStore,
  ResearchStore,
  ResearchStoreStatus,
  StoreIdType,
  StorePreset,
} from '../../pages/digital-shelf2/types';

type ResearchCompositeKey = {
  retailer_id: number;
  keyword_id: number;
  campaign_id: number;
};
export interface IState {
  items: any[];
  itemsAggregated: DsAggProduct[];
  itemsLoading: boolean;
  isRefreshDataButtonPending: boolean;
  scheduledTasks: any[];
  scheduledTasksLoading: boolean;
  resultsLog: any[];
  resultsLogLoading: boolean;
}

export const digitalShelfEndpoints = {
  getCampaignResearch: createAsyncThunk('digitalShelf/getCampaignResearch', async (payload: {campaign_id: string}) => {
    const response = await ApiService.get(`digitalshelf/getResearchList?${new URLSearchParams(payload)}`);
    return response.data as DsResearch[];
  }),
  getCampaignStores: createAsyncThunk('digitalShelf/getCampaignStores', async (payload: any) => {
    const response = await ApiService.post(`/inventory/digital-shelf-reports/choices/FetchStoreChoices`, payload);
    return response.data;
  }),
  getStoresByLookup: createAsyncThunk(
    'digitalShelf/getStoresByLookup',
    async (payload: {
      /** `Retailer.external_code` */
      retailer: string;
      storeLookupBy: 'tdlinx_store_code' | 'zip_code' | 'retailer_own_id';
      storeLookups: string[];
    }) => {
      if (payload.storeLookupBy === 'retailer_own_id') return payload.storeLookups;

      const response = await ApiService.post('/inventory/data-processing/api/storeLookupGetScrapeologyId', payload);
      return response.data as string[];
    },
  ),
  researchInvoke: createAsyncThunk(
    'digitalShelf/researchInvoke',
    async (
      payload: {
        page_depth: number;
        store_ids: number[];
      } & ResearchCompositeKey,
    ) => {
      const response = await ApiService.post(`/digital_shelf/research_invoke`, payload);
      return response.data;
    },
  ),

  createResearch: createAsyncThunk(
    'digitalShelf/createResearch',
    async (payload: {page_depth: number} & ResearchCompositeKey) => {
      const response = await ApiService.post(`/digital_shelf/research`, payload);
      return response.data as {research_id: number; keyword: DsKeyword};
    },
  ),
  updateResearch: createAsyncThunk(
    'digitalShelf/updateResearchStores',
    async (
      payload: {
        store_id_type: StoreIdType;
        store_ids: number[];
      } & ResearchCompositeKey,
    ) => {
      const response = await ApiService.put(`/digital_shelf/research`, payload);
      return response.data;
    },
  ),
  getResearch: createAsyncThunk(
    'digitalShelf/getResearch',
    async (payload: {retailer_id: string; keyword_id: string; campaign_id: string}) => {
      const response = await ApiService.get<DsResearch>(`/digital_shelf/research?${new URLSearchParams(payload)}`);
      return response.data;
    },
  ),
  getResearchStoreStatusList: createAsyncThunk(
    'digitalShelf/getResearchStoreStatusList',
    async (payload: {retailer_id: string; keyword_id: string; campaign_id: string}) => {
      const response = await ApiService.get<ResearchStore[]>(
        `/digital_shelf/research_store_status?${new URLSearchParams(payload)}`,
      );
      return response.data.map(it => {
        return {
          store: it,
          lookupStatus: 'confirmed',
        } as DsStore;
      });
    },
  ),
  getCampaignKeywords: createAsyncThunk('digitalShelf/campaign_keywords', async (payload: {campaign_id: string}) => {
    const response = await ApiService.get(`/digital_shelf/campaign_keywords?${new URLSearchParams(payload)}`);
    return response.data;
  }),
  getOrCreateKeyword: createAsyncThunk('digitalShelf/createKeyword', async (payload: {keyword: string}) => {
    const response = await ApiService.post<DsKeyword>(`/digital_shelf/keyword/get_or_create/`, payload);
    return response.data;
  }),
  getCampaignItemList: createAsyncThunk(
    'digitalShelf/getCampaignItemList',
    async (payload: {
      retailer?: string;
      pathId: string;
      storeLookupBy: string;
      storeLookupSingle?: string;
      keyword: string;
    }) => {
      const response = await ApiService.post(`/inventory/digital-shelf-reports/products/FilterProductsBasic`, payload);
      return response.data;
    },
  ),
  getResearchPending: createAsyncThunk('digitalShelf/getResearchPending', async (payload: {research_id: any}) => {
    const response = await ApiService.get(`/digitalshelf/getResearchScraperTaskStatus?${new URLSearchParams(payload)}`);
    return response.data as ResearchStoreStatus[];
  }),
  getRetailers: createAsyncThunk('digitalShelf/getRetailers', async (params: any) => {
    const response = await ApiService.get(`/retailers/?${new URLSearchParams(params)}`);
    return response.data;
  }),
  getStoresInRadius: createAsyncThunk(
    'digitalShelf/getStoresInRadius',
    async (payload: {zip_code: number; radius_miles: number; retailer_id: number | string | undefined}) => {
      const response = await ApiService.get<{id: number; retailer_store_id: string}[]>(
        `/digital_shelf/store_locator?${new URLSearchParams(payload as any)}`,
      );
      return response.data;
    },
  ),
  createStoreList: createAsyncThunk(
    'digitalShelf/createStoreList',
    async (payload: {name: string; stores: number[]; campaign: any; retailer: any; preset: boolean}) => {
      const response = await ApiService.post(`/p_digital_shelf/store_list/`, payload);
      return response.data;
    },
  ),
  getStoreLists: createAsyncThunk(
    'digitalShelf/getStoreLists',
    async (payload: {campaign?: any; retailer: number; preset: boolean; campaign__isnull?: boolean}) => {
      const response = await ApiService.get(`/p_digital_shelf/store_list/`, {params: payload});
      return response.data.results as StorePreset[];
    },
  ),
  deleteStoreList: createAsyncThunk('digitalShelf/deleteStoreLists', async (id: any) => {
    const response = await ApiService.delete(`/p_digital_shelf/store_list/${id}`);
    return response.data;
  }),
  createScheduleConfiguration: createAsyncThunk('digitalShelf/createScheduleConfiguration', async (params: any) => {
    const response = await ApiService.post(`/p_digital_shelf/automation/`, params);
    return response.data;
  }),
  updateScheduleConfiguration: createAsyncThunk('digitalShelf/updateScheduleConfiguration', async (params: any) => {
    const response = await ApiService.patch(`/p_digital_shelf/automation/${params.id}/`, params);
    return response.data;
  }),
  getScheduleConfigurations: createAsyncThunk('digitalShelf/getScheduleConfigurations', async (params: any) => {
    const response = await ApiService.get(`/p_digital_shelf/automation?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),
  getScheduleConfiguration: createAsyncThunk('digitalShelf/getScheduleConfiguration', async (id: any) => {
    const response = await ApiService.get(`/p_digital_shelf/automation/${id}/`);
    return response.data;
  }),
  deleteScheduleConfiguration: createAsyncThunk('digitalShelf/deleteScheduleConfiguration', async (params: any) => {
    const response = await ApiService.delete(`/p_digital_shelf/automation/${params.id}`);
    return response.data;
  }),
  getScheduleDayResults: createAsyncThunk('digitalShelf/getScheduleDayResults', async (params: any) => {
    const response = await ApiService.get(`/p_digital_shelf/automation_day?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),
  getDayResultsLogs: createAsyncThunk('digitalShelf/getDayResultsLogs', async (params: any) => {
    const response = await ApiService.post(
      `/inventory/inventory-reports/reportLiveFulFillment/reportAutomatedFailures`,
      params,
    );
    return response.data;
  }),
  getResearchResultsAggregated: createAsyncThunk(
    'digitalShelf/getResearchResultsAggregated',
    async (payload: {store_ids: string; retailer_id: string; keyword_id: string; campaign_id: string}) => {
      const response = await ApiService.get(`/digitalshelf/products_aggregated?${new URLSearchParams(payload)}`);
      return response.data as DsProduct[];
    },
  ),
  searchStoreByExternalId: createAsyncThunk(
    'digitalShelf/searchStoreByExternalId',
    async (payload: {store_id_type: StoreIdType; external_store_ids: string[]; retailer_id: string}) => {
      const response = await ApiService.post<Record<string, {id: number; retailer_store_id: string} | null>>(
        `/digital_shelf/search_store`,
        payload,
      );
      return response.data;
    },
  ),
  searchStoreByZipCodes: createAsyncThunk(
    'digitalShelf/searchStoreByZipCodes',
    async (payload: {zip_codes: number[]; retailer_id: string}) => {
      const response = await ApiService.post<{id: number; retailer_store_id: string; zip_code: string}[]>(
        `/digital_shelf/search_store_by_zip`,
        payload,
      );
      return response.data;
    },
  ),
};
export const digitalShelfSlice = createSlice({
  name: 'digitalShelf',
  initialState: {
    items: [],
    itemsLoading: false,
    itemsAggregated: [],
    scheduledTasks: [],
    scheduledTasksLoading: false,
    resultsLog: [],
    resultsLogLoading: false,
    isRefreshDataButtonPending: true,
  } as IState,
  reducers: {
    clearCampaignItemList(state) {
      state.items = [];
      state.itemsAggregated = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(digitalShelfEndpoints.getCampaignItemList.pending, state => {
        state.itemsLoading = true;
      })
      .addCase(digitalShelfEndpoints.getCampaignItemList.fulfilled, (state, {payload}) => {
        // state.itemsLoading = false;
        state.items = payload.map((it, i) => ({...it, id: i}));
      })
      .addCase(digitalShelfEndpoints.getCampaignItemList.rejected, state => {
        // state.itemsLoading = false;
        state.items = [];
      })
      .addCase(digitalShelfEndpoints.getScheduleConfigurations.pending, state => {
        state.scheduledTasksLoading = true;
      })
      .addCase(digitalShelfEndpoints.getScheduleConfigurations.fulfilled, (state, {payload}) => {
        state.scheduledTasksLoading = false;
        state.scheduledTasks = payload.results;
      })
      .addCase(digitalShelfEndpoints.getScheduleConfigurations.rejected, state => {
        state.scheduledTasksLoading = false;
      })
      .addCase(digitalShelfEndpoints.getDayResultsLogs.pending, state => {
        state.resultsLogLoading = true;
      })

      .addCase(digitalShelfEndpoints.getDayResultsLogs.fulfilled, (state, {payload}) => {
        state.resultsLogLoading = false;
        state.resultsLog = payload?.filter(it => Object.keys(it).length > 0);
      })
      .addCase(digitalShelfEndpoints.getDayResultsLogs.rejected, state => {
        state.resultsLogLoading = false;
        state.resultsLog = [];
      })
      .addCase(digitalShelfEndpoints.getResearchPending.fulfilled, (state, {payload}) => {
        state.isRefreshDataButtonPending = !payload.every(s => s.status === 'DATA_PARSED' || s.status === 'ERROR');
      })
      .addCase(digitalShelfEndpoints.getResearchResultsAggregated.pending, state => {
        state.itemsLoading = true;
      })
      .addCase(digitalShelfEndpoints.getResearchResultsAggregated.fulfilled, (state, {payload}) => {
        // if (state.isRefreshDataButtonPending) return;

        state.itemsLoading = false;
        state.itemsAggregated = payload.map(it => ({
          retailer_item_id: it.retailer_item_id,
          min_rank: it.min_rank,
          avg_rank: it.avg_rank,
          max_rank: it.max_rank,
          min_sale_price: it.min_price,
          avg_sale_price: it.avg_price,
          max_sale_price: it.max_price,
          name: it.title,
          retailer_image: it.image_url,
          sponsored_min_rank: it.sp_min_rank,
          sponsored_avg_rank: it.sp_avg_rank,
          sponsored_max_rank: it.sp_max_rank,
        }));
      })
      .addCase(digitalShelfEndpoints.getResearchResultsAggregated.rejected, state => {
        state.itemsLoading = false;
        // state.itemsAggregated = [];
      });
  },
});

export const digitalShelfActions = digitalShelfSlice.actions;

export type DigitalShelfSlice = {
  [digitalShelfSlice.name]: ReturnType<(typeof digitalShelfSlice)['reducer']>;
};
